import {
  decodeFixedBase64Url,
  DEFAULT_LINK_MAX_BYTES,
  LINK_API_PATH,
  LINK_ARTIFACT_API_PATH,
  LINK_ARTIFACT_CONTENT_TYPE,
  LINK_DIGEST_HEADER,
  LINK_EXPIRES_HEADER,
  LINK_MAX_BYTES_HEADER,
  LINK_PROTOCOL_VERSION,
  LINK_REVOKE_HEADER,
  LINK_SERVICE_HEADER,
  LINK_VERSION_HEADER,
  type LinkErrorResponse,
  type LinkServiceEnvironment,
  type LinkUploadResponse,
} from "../../link-protocol";

export interface LinkTransferProgress {
  loaded: number;
  total: number;
}

export interface UploadEncryptedArtifactInput {
  artifact: Blob;
  accessToken: string;
  revokeToken: string;
  ciphertextSha256: string;
  signal?: AbortSignal;
  onProgress?: (progress: LinkTransferProgress) => void;
}

export interface DownloadEncryptedArtifactInput {
  accessToken: string;
  signal?: AbortSignal;
  onProgress?: (progress: LinkTransferProgress) => void;
}

export interface DownloadEncryptedArtifactResult {
  artifact: Blob;
  ciphertextSha256: string;
  expiresAt: string;
  environment: LinkServiceEnvironment;
}

export interface LinkArtifactMetadata {
  ciphertextBytes: number;
  ciphertextSha256: string;
  expiresAt: string;
  environment: LinkServiceEnvironment;
}

export interface LinkServiceStatus {
  environment: LinkServiceEnvironment;
  maxBytes: number;
}

export interface LinkApiClient {
  inspectService(signal?: AbortSignal): Promise<LinkServiceStatus>;
  uploadEncryptedArtifact(
    input: UploadEncryptedArtifactInput,
  ): Promise<LinkUploadResponse>;
  downloadEncryptedArtifact(
    input: DownloadEncryptedArtifactInput,
  ): Promise<DownloadEncryptedArtifactResult>;
  inspectArtifact(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<LinkArtifactMetadata>;
  revoke(accessToken: string, revokeToken: string, signal?: AbortSignal): Promise<void>;
}

export interface LinkApiClientOptions {
  baseUrl?: string | URL;
  fetch?: typeof fetch;
  maxDownloadBytes?: number;
}

export class LinkApiError extends Error {
  public readonly code: LinkErrorResponse["code"] | "invalid_response";
  public readonly status: number;

  public constructor(code: LinkApiError["code"], message: string, status: number) {
    super(message);
    this.name = "LinkApiError";
    this.code = code;
    this.status = status;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function endpoint(baseUrl: string | URL | undefined, path: string): string {
  if (baseUrl === undefined) {
    return path;
  }
  return new URL(path, baseUrl).toString();
}

function requestHeaders(accessToken: string): Headers {
  return new Headers({
    Authorization: `Bearer ${accessToken}`,
    [LINK_VERSION_HEADER]: String(LINK_PROTOCOL_VERSION),
  });
}

function parseEnvironment(value: unknown): LinkServiceEnvironment {
  if (value === "cloudflare" || value === "local-simulation") {
    return value;
  }
  throw new LinkApiError(
    "invalid_response",
    "The link service returned an invalid environment.",
    502,
  );
}

function parsePositiveInteger(value: string | null, label: string): number {
  if (
    value === null ||
    !/^[1-9][0-9]*$/u.test(value) ||
    !Number.isSafeInteger(Number(value))
  ) {
    throw new LinkApiError(
      "invalid_response",
      `The link service returned an invalid ${label}.`,
      502,
    );
  }
  return Number(value);
}

function parseExpiry(value: unknown): string {
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) {
    throw new LinkApiError(
      "invalid_response",
      "The link service returned an invalid expiry time.",
      502,
    );
  }
  return value;
}

function parseDigest(value: unknown): string {
  if (typeof value !== "string") {
    throw new LinkApiError(
      "invalid_response",
      "The link service returned an invalid ciphertext digest.",
      502,
    );
  }
  try {
    decodeFixedBase64Url(value, 32, "Ciphertext digest");
  } catch {
    throw new LinkApiError(
      "invalid_response",
      "The link service returned an invalid ciphertext digest.",
      502,
    );
  }
  return value;
}

async function throwResponseError(response: Response): Promise<never> {
  let body: unknown;
  try {
    body = (await response.json()) as unknown;
  } catch {
    throw new LinkApiError(
      "invalid_response",
      "The link service returned an unreadable error.",
      response.status,
    );
  }

  if (
    isRecord(body) &&
    typeof body.code === "string" &&
    typeof body.message === "string" &&
    [
      "bad_request",
      "expired_or_unavailable",
      "payload_too_large",
      "rate_limited",
      "storage_unavailable",
    ].includes(body.code)
  ) {
    throw new LinkApiError(
      body.code as LinkErrorResponse["code"],
      body.message,
      response.status,
    );
  }

  throw new LinkApiError(
    "invalid_response",
    "The link service returned an invalid error.",
    response.status,
  );
}

function parseUploadResponse(value: unknown): LinkUploadResponse {
  if (
    !isRecord(value) ||
    value.version !== LINK_PROTOCOL_VERSION ||
    typeof value.ciphertextBytes !== "number" ||
    !Number.isSafeInteger(value.ciphertextBytes) ||
    value.ciphertextBytes <= 0
  ) {
    throw new LinkApiError(
      "invalid_response",
      "The link service returned an invalid upload confirmation.",
      502,
    );
  }

  return {
    version: LINK_PROTOCOL_VERSION,
    expiresAt: parseExpiry(value.expiresAt),
    ciphertextBytes: value.ciphertextBytes,
    ciphertextSha256: parseDigest(value.ciphertextSha256),
    environment: parseEnvironment(value.environment),
  };
}

function environmentFromHeaders(headers: Headers): LinkServiceEnvironment {
  return parseEnvironment(headers.get(LINK_SERVICE_HEADER));
}

function metadataFromHeaders(headers: Headers): LinkArtifactMetadata {
  return {
    ciphertextBytes: parsePositiveInteger(
      headers.get("Content-Length"),
      "ciphertext length",
    ),
    ciphertextSha256: parseDigest(headers.get(LINK_DIGEST_HEADER)),
    expiresAt: parseExpiry(headers.get(LINK_EXPIRES_HEADER)),
    environment: environmentFromHeaders(headers),
  };
}

const storagePropagationRetryDelays = [
  1_000, 2_000, 4_000, 8_000, 15_000, 15_000, 15_000,
] as const;

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new DOMException("The request was cancelled.", "AbortError"));
      return;
    }

    const timer = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, milliseconds);
    const handleAbort = () => {
      globalThis.clearTimeout(timer);
      signal?.removeEventListener("abort", handleAbort);
      reject(new DOMException("The request was cancelled.", "AbortError"));
    };
    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

async function fetchWithStoragePropagationRetry(
  fetchImplementation: typeof fetch,
  url: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<Response> {
  for (const delay of storagePropagationRetryDelays) {
    const response = await fetchImplementation(url, init);
    if (response.status !== 503) {
      return response;
    }
    await response.body?.cancel();
    await abortableDelay(delay, signal);
  }
  return fetchImplementation(url, init);
}

function uploadWithXmlHttpRequest(
  url: string,
  input: UploadEncryptedArtifactInput,
): Promise<LinkUploadResponse> {
  return new Promise<LinkUploadResponse>((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;

    const handleSignalAbort = () => {
      request.abort();
    };
    const cleanup = () => {
      input.signal?.removeEventListener("abort", handleSignalAbort);
    };
    const finishResolve = (value: LinkUploadResponse) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(value);
    };
    const finishReject = (reason: unknown) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(reason instanceof Error ? reason : new Error("The link upload failed."));
    };

    request.open("POST", url);
    request.setRequestHeader("Authorization", `Bearer ${input.accessToken}`);
    request.setRequestHeader("Content-Type", LINK_ARTIFACT_CONTENT_TYPE);
    request.setRequestHeader(LINK_REVOKE_HEADER, input.revokeToken);
    request.setRequestHeader(LINK_DIGEST_HEADER, input.ciphertextSha256);
    request.setRequestHeader(LINK_VERSION_HEADER, String(LINK_PROTOCOL_VERSION));
    request.upload.addEventListener("progress", (event) => {
      input.onProgress?.({
        loaded: Math.min(event.loaded, input.artifact.size),
        total: input.artifact.size,
      });
    });
    request.addEventListener("abort", () => {
      finishReject(new DOMException("The upload was cancelled.", "AbortError"));
    });
    request.addEventListener("error", () => {
      finishReject(
        new LinkApiError(
          "storage_unavailable",
          "The link service could not be reached.",
          0,
        ),
      );
    });
    request.addEventListener("load", () => {
      void (async () => {
        if (request.status < 200 || request.status >= 300) {
          await throwResponseError(
            new Response(request.responseText, {
              status: request.status,
              headers: {
                "Content-Type":
                  request.getResponseHeader("Content-Type") ?? "application/json",
              },
            }),
          );
        }

        let value: unknown;
        try {
          value = JSON.parse(request.responseText) as unknown;
        } catch {
          throw new LinkApiError(
            "invalid_response",
            "The link service returned an unreadable confirmation.",
            request.status,
          );
        }
        const parsed = parseUploadResponse(value);
        if (
          parsed.ciphertextBytes !== input.artifact.size ||
          parsed.ciphertextSha256 !== input.ciphertextSha256
        ) {
          throw new LinkApiError(
            "invalid_response",
            "The upload confirmation did not match the encrypted file.",
            502,
          );
        }
        input.onProgress?.({
          loaded: input.artifact.size,
          total: input.artifact.size,
        });
        finishResolve(parsed);
      })().catch(finishReject);
    });

    if (input.signal?.aborted === true) {
      finishReject(new DOMException("The upload was cancelled.", "AbortError"));
      return;
    }
    input.signal?.addEventListener("abort", handleSignalAbort, { once: true });
    input.onProgress?.({ loaded: 0, total: input.artifact.size });
    request.send(input.artifact);
  });
}

async function readArtifactBody(
  response: Response,
  metadata: LinkArtifactMetadata,
  maximumBytes: number,
  onProgress?: (progress: LinkTransferProgress) => void,
): Promise<Blob> {
  if (metadata.ciphertextBytes > maximumBytes) {
    throw new LinkApiError(
      "payload_too_large",
      "The encrypted download exceeds the configured limit.",
      413,
    );
  }
  if (response.body === null) {
    throw new LinkApiError(
      "invalid_response",
      "The link service returned an empty encrypted download.",
      502,
    );
  }

  onProgress?.({ loaded: 0, total: metadata.ciphertextBytes });
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let loaded = 0;

  while (true) {
    const result = await reader.read();
    if (result.done) {
      break;
    }
    const chunk = result.value.slice();
    loaded += chunk.byteLength;
    if (loaded > metadata.ciphertextBytes || loaded > maximumBytes) {
      await reader.cancel();
      throw new LinkApiError(
        "invalid_response",
        "The encrypted download length did not match.",
        502,
      );
    }
    chunks.push(chunk);
    onProgress?.({ loaded, total: metadata.ciphertextBytes });
  }

  if (loaded !== metadata.ciphertextBytes) {
    throw new LinkApiError(
      "invalid_response",
      "The encrypted download length did not match.",
      502,
    );
  }
  return new Blob(chunks, { type: LINK_ARTIFACT_CONTENT_TYPE });
}

export function createLinkApiClient(options: LinkApiClientOptions = {}): LinkApiClient {
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  const maxDownloadBytes = options.maxDownloadBytes ?? DEFAULT_LINK_MAX_BYTES;
  if (
    typeof fetchImplementation !== "function" ||
    !Number.isSafeInteger(maxDownloadBytes) ||
    maxDownloadBytes <= 0
  ) {
    throw new Error("The link API client configuration is invalid.");
  }

  return {
    async inspectService(signal) {
      const response = await fetchImplementation(
        endpoint(options.baseUrl, LINK_API_PATH),
        {
          method: "HEAD",
          cache: "no-store",
          credentials: "same-origin",
          referrerPolicy: "no-referrer",
          signal: signal ?? null,
        },
      );
      if (!response.ok) {
        return throwResponseError(response);
      }
      return {
        environment: environmentFromHeaders(response.headers),
        maxBytes: parsePositiveInteger(
          response.headers.get(LINK_MAX_BYTES_HEADER),
          "link byte limit",
        ),
      };
    },

    async uploadEncryptedArtifact(input) {
      if (options.fetch === undefined && typeof XMLHttpRequest === "function") {
        return uploadWithXmlHttpRequest(
          endpoint(options.baseUrl, LINK_API_PATH),
          input,
        );
      }

      const requestHeadersValue = requestHeaders(input.accessToken);
      requestHeadersValue.set("Content-Type", LINK_ARTIFACT_CONTENT_TYPE);
      requestHeadersValue.set(LINK_REVOKE_HEADER, input.revokeToken);
      requestHeadersValue.set(LINK_DIGEST_HEADER, input.ciphertextSha256);

      input.onProgress?.({ loaded: 0, total: input.artifact.size });
      const response = await fetchImplementation(
        endpoint(options.baseUrl, LINK_API_PATH),
        {
          method: "POST",
          headers: requestHeadersValue,
          body: input.artifact,
          cache: "no-store",
          credentials: "same-origin",
          referrerPolicy: "no-referrer",
          signal: input.signal ?? null,
        },
      );
      if (!response.ok) {
        return throwResponseError(response);
      }

      const parsed = parseUploadResponse((await response.json()) as unknown);
      if (
        parsed.ciphertextBytes !== input.artifact.size ||
        parsed.ciphertextSha256 !== input.ciphertextSha256
      ) {
        throw new LinkApiError(
          "invalid_response",
          "The upload confirmation did not match the encrypted file.",
          502,
        );
      }
      input.onProgress?.({
        loaded: input.artifact.size,
        total: input.artifact.size,
      });
      return parsed;
    },

    async downloadEncryptedArtifact(input) {
      const response = await fetchWithStoragePropagationRetry(
        fetchImplementation,
        endpoint(options.baseUrl, LINK_ARTIFACT_API_PATH),
        {
          method: "GET",
          headers: requestHeaders(input.accessToken),
          cache: "no-store",
          credentials: "same-origin",
          referrerPolicy: "no-referrer",
          signal: input.signal ?? null,
        },
        input.signal,
      );
      if (!response.ok) {
        return throwResponseError(response);
      }

      const metadata = metadataFromHeaders(response.headers);
      return {
        artifact: await readArtifactBody(
          response,
          metadata,
          maxDownloadBytes,
          input.onProgress,
        ),
        ciphertextSha256: metadata.ciphertextSha256,
        expiresAt: metadata.expiresAt,
        environment: metadata.environment,
      };
    },

    async inspectArtifact(accessToken, signal) {
      const response = await fetchImplementation(
        endpoint(options.baseUrl, LINK_ARTIFACT_API_PATH),
        {
          method: "HEAD",
          headers: requestHeaders(accessToken),
          cache: "no-store",
          credentials: "same-origin",
          referrerPolicy: "no-referrer",
          signal: signal ?? null,
        },
      );
      if (!response.ok) {
        return throwResponseError(response);
      }
      return metadataFromHeaders(response.headers);
    },

    async revoke(accessToken, revokeToken, signal) {
      const requestHeadersValue = requestHeaders(accessToken);
      requestHeadersValue.set(LINK_REVOKE_HEADER, revokeToken);
      const response = await fetchImplementation(
        endpoint(options.baseUrl, LINK_API_PATH),
        {
          method: "DELETE",
          headers: requestHeadersValue,
          cache: "no-store",
          credentials: "same-origin",
          referrerPolicy: "no-referrer",
          signal: signal ?? null,
        },
      );
      if (!response.ok) {
        return throwResponseError(response);
      }
    },
  };
}
